import { cleanHtmlTags, parseDate, parseNumber } from '../utils/formatting.js';
import { hexToBackgroundAttributes, getColorStyle, createAggregationCellStyle } from '../utils/colors.js';
import { EXCEL_STYLES } from '../constants.js';

export const createFieldHandlers = (colorMap) => ({
  text: (value, column) => ({
    value: cleanHtmlTags(value),
    colWidth: column.colWidth || 11
  }),

  middleText: (value, column) => ({
    value: cleanHtmlTags(value),
    colWidth: column.colWidth || 22
  }),

  longText: (value, column) => ({
    value: cleanHtmlTags(value),
    colWidth: column.colWidth || 44
  }),

  wbs: (value, column) => ({
    value: value || '',
    colWidth: column.colWidth || 15,
    style: { alignment: { horizontal: 'left', vertical: 'middle' } }
  }),

  color: (value, column) => {
    const hex = colorMap[value] || value;
    const result = { value: hex || '', colWidth: column.colWidth || 12 };
    if (hex) result.style = hexToBackgroundAttributes(hex);
    result.style = { ...result.style, alignment: { indent: 3 } };
    return result;
  },

  hex: (value, column) => {
    const result = { value: value || '', colWidth: column.colWidth || 12 };
    if (value) result.style = hexToBackgroundAttributes(value);
    return result;
  },

  numberWithBackground: (value, column) => {
    const floatNumber = parseNumber(value);
    const result = {
      value: floatNumber !== null ? floatNumber : value || '',
      colWidth: column.colWidth || 15,
      style: {
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
          top: { style: 'thin', color: { argb: EXCEL_STYLES.GRID_BORDER } },
          bottom: { style: 'thin', color: { argb: EXCEL_STYLES.GRID_BORDER } },
          right: { style: 'thin', color: { argb: EXCEL_STYLES.GRID_BORDER } },
          left: { style: 'thin', color: { argb: EXCEL_STYLES.GRID_BORDER } }
        }
      }
    };
    return result;
  },

  date: (value, column) => {
    const dateObj = parseDate(value);
    return { value: dateObj || '', colWidth: column.colWidth || 11 };
  },

  number: (value, column) => {
    const floatNumber = parseNumber(value);
    const isNegative = !isNaN(value) && value < 0;
    const style = {
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
    if (isNegative) {
      style.font = { color: { argb: 'FFFF0000' } };
    }
    if (floatNumber !== null) return { value: floatNumber, colWidth: column.colWidth || 8, style };
    const resultValue = value === '' ? 0 : value;
    return { value: resultValue || '0', colWidth: column.colWidth || 8, style };
  },

  type: (value, isParent = false) => {
    if (isParent) {
      return {
        value: '',
        colWidth: 12,
        style: { font: { bold: true, color: { argb: EXCEL_STYLES.BLACK_TEXT } } }
      };
    }

    if (value === 'milestone') {
      const colorStyle = getColorStyle('MILESTONE_BG', colorMap);
      return {
        value: '',
        colWidth: 12,
        style: {
          fill: colorStyle.fill || {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_STYLES.MILESTONE_BG }
          }
        }
      };
    } else if (value === 'task') {
      const colorStyle = getColorStyle('TASK_BG', colorMap);
      return {
        value: '',
        colWidth: 12,
        style: {
          fill: colorStyle.fill || {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_STYLES.TASK_BG }
          }
        }
      };
    }
    return { value: '', colWidth: 12 };
  },

  level: (value) => ({
    value: value || 0,
    colWidth: 8,
    style: {
      alignment: { horizontal: 'center', vertical: 'middle' },
      font: { bold: true, color: { argb: EXCEL_STYLES.LEVEL_TEXT } }
    }
  }),

  aggregation: (value, column, context = {}) => {
    const { isGroupHeader = false, isParent = false } = context;
    const numValue = parseFloat(value);
    const isNegative = !isNaN(numValue) && numValue < 0;

    // Базовый стиль
    const style = {
      alignment: { horizontal: 'center', vertical: 'middle' },
      font: { bold: isGroupHeader || isParent ? true : false },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: isGroupHeader ? EXCEL_STYLES.GROUP_AGGREGATION_BG : EXCEL_STYLES.AGGREGATION_BG }
      },
      border: { // Добавляем ей границы
        top: { style: 'thin', color: { argb: "D0D0D0" } },
        bottom: { style: 'thin', color: { argb: "D0D0D0" } },
        left: { style: 'thin', color: { argb: "D0D0D0" } },
        right: { style: 'thin', color: { argb: "D0D0D0" } }
      }
    };
    // Добавляем красный цвет для отрицательных
    if (isNegative) {
      const negativeColor = column.negativeColor || 'FFFF0000';
      style.font.color = { argb: negativeColor };
    }

    const resultValue = value === '' || value === 0 ? '0' : value;

    return {
      value: resultValue,
      colWidth: column.colWidth || 11,
      style
    };
  },

  // Специализированный обработчик для групп
  groupAggregation: (value, column, customBg) => {
    const numValue = parseFloat(value);
    const isNegative = !isNaN(numValue) && numValue < 0;

    const style = {
      alignment: { horizontal: 'center', vertical: 'middle' },
      font: { bold: true },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: customBg ? customBg : EXCEL_STYLES.GROUP_AGGREGATION_BG }
      },
      border: { // Добавляем ей границы
        top: { style: 'thin', color: { argb: "D0D0D0" } },
        bottom: { style: 'thin', color: { argb: "D0D0D0" } },
      }
    };

    if (isNegative) {
      const negativeColor = column.negativeColor || 'FFFF0000';
      style.font.color = { argb: negativeColor };
    }

    const resultValue = value === '' || value === 0 ? '0' : value;

    return {
      value: resultValue,
      colWidth: column.colWidth || 11,
      style
    };
  },

  default: (value, column) => ({
    value: value ? String(value) : '',
    colWidth: column.colWidth || 11
  })
});